import { EdgeItemType } from 'data/types';
import { RepeatWrapping, Texture } from 'three';

export type EdgeItemTextures = {
  doorWoodTex: Texture;
  doorMetalTex: Texture;
  garageDoorTex: Texture;
  petDoorTex: Texture;
};

export type EdgeItemDisplay = {
  isWindow: boolean;
  isDoor: boolean;
  isGlassDoor: boolean;
  itemSize: number;
  itemHeight: number;
  doorTexture: Texture;
  standsOnFloor: boolean;
  usesGlassMaterial: boolean;
  textureRepeatX: number;
};

export function glassDoorPaneCount(item: EdgeItemType): number {
  if (item.type !== 'glass-door') {
    return 1;
  }
  return item.glassPaneCount === 2 ? 2 : 1;
}

export function edgeItemMaterialMap(
  display: EdgeItemDisplay,
  windowTex: Texture,
  glassDoorTex: Texture = windowTex
): Texture {
  if (!display.usesGlassMaterial) {
    return display.doorTexture;
  }
  const glassTex = display.isGlassDoor ? glassDoorTex : windowTex;
  if (display.textureRepeatX <= 1) {
    return glassTex;
  }

  const tiled = glassTex.clone();
  tiled.wrapS = RepeatWrapping;
  tiled.wrapT = RepeatWrapping;
  tiled.repeat.set(display.textureRepeatX, 1);
  tiled.needsUpdate = true;
  return tiled;
}

export function computeEdgeItemDisplay(
  item: EdgeItemType,
  spanFt: number | undefined,
  textures: EdgeItemTextures
): EdgeItemDisplay {
  const isWindow = item.type === 'window';
  const isDoor = item.type === 'door';
  const isGlassDoor = item.type === 'glass-door';
  const width = item.width ?? 0;
  const isGarageDoor = isDoor && width >= 8;
  const isPetDoor = isDoor && width > 0 && width <= 2;
  const span = spanFt;

  let itemSize = 0.4;
  if (isWindow && span) {
    itemSize = Math.min(3, Math.max(0.6, span * 0.4));
  } else if (isGlassDoor) {
    itemSize = glassDoorPaneCount(item) === 2 ? 2 : 1;
  } else if (isGarageDoor && item.width) {
    itemSize = Math.min(8, Math.max(2, item.width * 0.4));
  } else if (isPetDoor && item.width) {
    itemSize = Math.min(1, Math.max(0.4, item.width * 0.4));
  } else if (isDoor) {
    itemSize = 1;
  }

  let itemHeight = isWindow ? 1 : 2;
  if (isPetDoor && item.height) {
    itemHeight = Math.min(1, Math.max(0.4, item.height * 0.4));
  }

  let doorTexture = textures.doorWoodTex;
  if (isDoor) {
    if (isGarageDoor) doorTexture = textures.garageDoorTex;
    else if (isPetDoor) doorTexture = textures.petDoorTex;
    else if (item.material === 'Metal') doorTexture = textures.doorMetalTex;
  }

  return {
    isWindow,
    isDoor,
    isGlassDoor,
    itemSize,
    itemHeight,
    doorTexture,
    standsOnFloor: isDoor || isGlassDoor,
    usesGlassMaterial: isWindow || isGlassDoor,
    textureRepeatX: glassDoorPaneCount(item)
  };
}
